import { ColorEnum } from '@/constants/color'
import { isIOS } from '@/constants/environment'
import { PriceItem } from '@/features/common/components/Overview/types'
import PriceItemsList from '@/features/pos/components/OrderFinalization/parts/BottomSheet/parts/PriceToPay/parts/PriceItemsList'
import Collapsible from '@/features/ui/components/Collapsible'
import ChevronDownIcon from '@/features/ui/icons/components/ChevronDownIcon'
import Typography from '@/features/ui/typography'
import { FC, PropsWithChildren, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'
import Animated, {
    Easing,
    useAnimatedStyle,
    useDerivedValue,
    withTiming,
} from 'react-native-reanimated'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface CollapsibleFooterProps extends PropsWithChildren {
    totalPrice: string
    priceItems: PriceItem[]
    testID: string
}

const CollapsibleFooter: FC<CollapsibleFooterProps> = ({
    children,
    totalPrice,
    priceItems,
    testID,
}) => {
    const { t } = useTranslation('app')
    const { styles } = useStyles(stylesheet)
    const [expanded, setExpanded] = useState(false)

    const rotation = useDerivedValue(() =>
        withTiming(expanded ? 0 : 180, {
            duration: 220,
            easing: Easing.out(Easing.cubic),
        })
    )

    const chevronStyle = useAnimatedStyle(() => ({
        transform: [{ rotate: `${rotation.value}deg` }],
    }))

    return (
        <View style={styles.container} testID={testID}>
            <Collapsible
                open={expanded}
                containerStyle={styles.collapsible}
                testID={`${testID}-content`}
            >
                <View
                    aria-hidden={!expanded}
                    accessibilityElementsHidden={!expanded}
                    importantForAccessibility={expanded ? 'auto' : 'no-hide-descendants'}
                >
                    <PriceItemsList items={priceItems} />
                </View>
            </Collapsible>
            <Pressable
                accessibilityLabel={t(
                    expanded ? 'openTabs.actions.collapseFooter' : 'openTabs.actions.expandFooter'
                )}
                accessibilityRole="button"
                accessibilityState={{ expanded }}
                onPress={() => setExpanded((current) => !current)}
                style={styles.totalRow}
                testID={`${testID}-toggle`}
            >
                <Typography.Heading variant="h3" style={styles.totalCell}>
                    {t('openTabs.summary.total')}
                </Typography.Heading>
                <Animated.View style={chevronStyle} testID={`${testID}-chevron`}>
                    <ChevronDownIcon width={24} height={24} />
                </Animated.View>
                <Typography.Heading variant="h3" style={[styles.totalCell, styles.totalPrice]}>
                    {totalPrice}
                </Typography.Heading>
            </Pressable>
            <View style={styles.content} testID={`${testID}-actions`}>
                {children}
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    container: {
        width: '100%',
        paddingTop: 8,
        paddingHorizontal: 16,
        paddingBottom: isIOS ? Math.max(16, insets.bottom) : 16 + insets.bottom,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        backgroundColor: ColorEnum.VIOLET_100,
    },
    totalRow: {
        width: '100%',
        minHeight: 42,
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: ColorEnum.GRAY_300,
    },
    totalCell: {
        flex: 1,
    },
    totalPrice: {
        textAlign: 'right',
    },
    collapsible: {
        width: '100%',
        overflow: 'hidden',
    },
    content: {
        width: '100%',
        gap: 12,
        paddingTop: 12,
    },
}))

export default CollapsibleFooter
